/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { EPerPages, ISortOptions } from 'models/table';
import { IPaginator } from 'models/paginator';
import {
  ILicenceFirearmsSearchResponseContent,
  ILicenceReadLimitsResponse,
  ILicencesFirearmsSearchForm,
  IWeaponLicenceFirearmToLink,
} from 'models/licencesFirearms';
import {
  getLicencesFirearmsSearchToLink,
  getReadLimitsData,
  weaponLicenceFirearmsToLink,
} from 'requests/licencesFirearms';
import { useTranslations } from 'hooks/useTranslations';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';
import { EAlertVariants } from 'components/atoms/Alert';
import LicenceStore from 'state/LicenceStore';
import { toSearchResponseFormat } from './helpers';

export const useLicencesFirearms = () => {
  const { t } = useTranslations();
  const { setAlert } = useAlert();
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [readLimits, setReadLimits] = useState<ILicenceReadLimitsResponse | null>(null);
  const [licencesFirearms, setLicencesFirearms] = useState<
    ILicenceFirearmsSearchResponseContent[] | undefined | null
  >();
  const [rawLicencesFirearms, setRawLicencesFirearms] = useState<
    ILicenceFirearmsSearchResponseContent[] | undefined | null
  >();
  const getLicencesSearchData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    query: Partial<ILicencesFirearmsSearchForm> = {},
    sort?: ISortOptions | null,
    licenceId?: string,
    isFromVaryRenew?: boolean,
  ): Promise<null> => {
    const res = await getLicencesFirearmsSearchToLink(currentPage, perPage, sort, query);
    if (res) {
      const { content, ...paginatorData } = res;
      setRawLicencesFirearms(content);

      let filteredContent = content;
      if (licenceId && isFromVaryRenew) {
        const weaponsAlreadySelectedToLink = LicenceStore.getFormWeapon(licenceId);
        const selectedWeaponIds =
          weaponsAlreadySelectedToLink?.map((weapon: any) => weapon.uniqueId) || [];

        filteredContent = content.filter(
          (weapon) => !selectedWeaponIds.includes(weapon.uniqueId),
        );

        const removedWeaponObjects = LicenceStore.getFormFirearmRemovedObjects(licenceId);
        if (removedWeaponObjects?.length) {
          const weaponsToAddBack = removedWeaponObjects.map(toSearchResponseFormat);
          filteredContent = [...filteredContent, ...weaponsToAddBack];
        }
      }

      setLicencesFirearms(filteredContent);
      setPaginator(paginatorData);
    } else {
      setRawLicencesFirearms(null);
      setLicencesFirearms(null);
      setPaginator(null);
    }
    return null;
  };

  const handleWeaponLicenceFirearmsToLink = async (
    data: string[],
    licenceId: string,
    shouldUpdateScheduled: boolean,
  ) => {
    try {
      const body: IWeaponLicenceFirearmToLink = {
        licenceUniqueId: licenceId,
        firearmToLinkUniqueIds: data,
        shouldUpdateScheduled,
      };
      const response = await weaponLicenceFirearmsToLink(body);
      if (response.status === 200) {
        setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('weaponLicenceFirearmsToLink.save.success.text'),
        });
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e?.response?.data.errors
          ? e?.response?.data.errors[0]?.message
          : e?.response?.data.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };

  const getReadLimits = async (id: string) => {
    const res = await getReadLimitsData(id);
    if (res) {
      setReadLimits(res);
    } else {
      setReadLimits(null);
    }
    return null;
  };

  return {
    paginator,
    licencesFirearms,
    getLicencesSearchData,
    handleWeaponLicenceFirearmsToLink,
    readLimits,
    getReadLimits,
  };
};
